/**
 * Vite Plugin: Agent Auto-Registration
 *
 * Provides the `virtual:smrt-agent-registrations` virtual module.
 * When imported, this module registers each agent's manifest with
 * AgentUIRegistry and imports the agent's `./admin` entry (if it exists)
 * for side-effect component registration.
 *
 * Agent packages are discovered from smrt.config.js's `agents` array,
 * or from the `agents` option passed directly to the plugin.
 *
 * @example
 * ```ts
 * // vite.config.ts
 * import { vitePluginAgentRoutes } from '@happyvertical/smrt-agents/vite';
 *
 * export default defineConfig({
 *   plugins: [
 *     sveltekit(),
 *     vitePluginAgentRoutes({
 *       agents: [
 *         { packageName: '@happyvertical/praeco', agentClass: 'Praeco' },
 *       ],
 *     }),
 *   ],
 * });
 * ```
 *
 * ```ts
 * // In your app:
 * import 'virtual:smrt-agent-registrations';
 * ```
 */

import { existsSync, readFileSync } from 'node:fs';
import { createRequire } from 'node:module';
import { dirname, resolve } from 'node:path';
import {
  extractAgentPackagesFromConfig,
  type PackageManifest,
} from './server/manifest-utils.js';

const VIRTUAL_MODULE_ID = 'virtual:smrt-agent-registrations';
const RESOLVED_VIRTUAL_MODULE_ID = `\0${VIRTUAL_MODULE_ID}`;

/** Convert a package name to a safe JS identifier (e.g., '@test/with-admin' → 'test_with_admin') */
function toSafeIdentifier(packageName: string): string {
  return packageName.replace('@', '').replace(/[/.-]/g, '_');
}

export interface AgentRoutesPluginOptions {
  /** Path to smrt.config.js (default: <project root>/smrt.config.js) */
  configPath?: string;
  /**
   * Agent packages to register. Falls back to reading smrt.config.js `agents` array
   * if not provided. When provided, `agentClass` is used as a hint but the plugin
   * still reads each package's manifest to discover the actual agent class.
   */
  agents?: Array<{
    packageName: string;
    agentClass: string;
  }>;
}

/**
 * Vite plugin that auto-registers agent manifests and admin components.
 *
 * Provides `virtual:smrt-agent-registrations` — import it to register
 * all agents declared in smrt.config.js.
 */
export function vitePluginAgentRoutes(options: AgentRoutesPluginOptions = {}): {
  name: string;
  resolveId: (id: string) => string | undefined;
  load: (id: string) => string | undefined;
  configResolved?: (config: { root: string }) => void;
} {
  let projectRoot = process.cwd();
  let agentPackages: string[] = [];

  return {
    name: 'smrt-agent-routes',

    configResolved(config: { root: string }) {
      projectRoot = config.root;

      // Try to load smrt.config.js using shared config extraction
      const configPath =
        options.configPath || resolve(projectRoot, 'smrt.config.js');

      try {
        agentPackages = extractAgentPackagesFromConfig(configPath);
      } catch {
        // Config not found or unreadable — fall back to options.agents
      }

      // Fall back to legacy options.agents if config didn't provide packages
      if (agentPackages.length === 0 && options.agents) {
        agentPackages = options.agents.map((a) => a.packageName);
      }

      if (agentPackages.length > 0) {
        console.log(
          `[smrt-agent-routes] Discovered ${agentPackages.length} agents: ${agentPackages.map((p) => p.split('/').pop()).join(', ')}`,
        );
      }
    },

    resolveId(id: string) {
      if (id === VIRTUAL_MODULE_ID) {
        return RESOLVED_VIRTUAL_MODULE_ID;
      }
    },

    load(id: string) {
      if (id !== RESOLVED_VIRTUAL_MODULE_ID) return;

      if (agentPackages.length === 0) {
        return `// No agents configured in smrt.config.js\nexport const agentAdminRegistry = new Map();\nexport function initializeAgentRegistrations() {}\n`;
      }

      const lines: string[] = [
        '/**',
        ' * Auto-generated agent registrations',
        ` * Source: smrt.config.js (${agentPackages.length} agents)`,
        ' * Generated by @happyvertical/smrt-agents/vite',
        ' */',
        "import { AgentUIRegistry } from '@happyvertical/smrt-agents/ui';",
        '',
      ];

      // For each agent package, try to resolve its manifest and find the agent class
      const registrations: Array<{
        packageName: string;
        manifestVar: string;
        agentClass: string | null;
        hasAdmin: boolean;
      }> = [];

      for (let i = 0; i < agentPackages.length; i++) {
        const pkg = agentPackages[i];
        const safeName = toSafeIdentifier(pkg);
        const manifestVar = `manifest_${safeName}`;

        // Try to read the manifest to extract the agent class name
        let agentClass: string | null = null;
        let manifestPath: string | null = null;
        try {
          const localRequire = createRequire(
            resolve(projectRoot, 'package.json'),
          );
          manifestPath = localRequire.resolve(`${pkg}/manifest`);
          const manifestContent = readFileSync(manifestPath, 'utf-8');
          const manifest: PackageManifest = JSON.parse(manifestContent);

          // Find the agent object (one with agent metadata)
          for (const obj of Object.values(manifest.objects)) {
            if (obj.agent) {
              agentClass = obj.className;
              break;
            }
          }
        } catch {
          // Manifest not found or unparseable — skip this agent
          console.warn(
            `[smrt-agent-routes] Could not read manifest for ${pkg} — skipping`,
          );
          continue;
        }

        if (!agentClass) {
          console.warn(
            `[smrt-agent-routes] No agent object found in manifest for ${pkg} — skipping`,
          );
          continue;
        }

        // Check if ./admin export exists by reading the package's exports field.
        // We can't use createRequire().resolve() because CJS resolution doesn't
        // recognise 'svelte' or 'import' conditions — only 'default'/'require'/'node'.
        // Derive the package root from the manifest path we already resolved.
        let hasAdmin = false;
        try {
          let pkgDir = dirname(manifestPath!);
          while (!existsSync(resolve(pkgDir, 'package.json'))) {
            const parent = dirname(pkgDir);
            if (parent === pkgDir) {
              break;
            }
            pkgDir = parent;
          }
          const pkgJson = JSON.parse(
            readFileSync(resolve(pkgDir, 'package.json'), 'utf-8'),
          );
          hasAdmin = !!pkgJson.exports?.['./admin'];
        } catch {
          // Failed to read package.json or parse exports — treat as no admin export
        }

        registrations.push({
          packageName: pkg,
          manifestVar,
          agentClass,
          hasAdmin,
        });

        // Import manifest
        lines.push(`import ${manifestVar} from '${pkg}/manifest';`);
      }

      lines.push('');

      // Generate the extractAgentManifest helper inline in the virtual module.
      // This runs in the browser, so it can't import from ./server.
      lines.push(
        'function extractAgentManifest(manifest, agentClass) {',
        '  for (const obj of Object.values(manifest.objects)) {',
        '    if (obj.className === agentClass && obj.agent) {',
        '      return obj.agent;',
        '    }',
        '  }',
        '  return null;',
        '}',
        '',
      );

      // Register manifests
      lines.push('// Register agent manifests');
      for (const { manifestVar, agentClass } of registrations) {
        const key = JSON.stringify(agentClass);
        lines.push(
          `{`,
          `  const m = extractAgentManifest(${manifestVar}, ${key});`,
          `  if (m) AgentUIRegistry.registerManifest(${key}, m);`,
          `}`,
        );
      }
      lines.push('');

      // Import admin modules — named imports for AgentAdminExport contract,
      // plus side-effect registration of legacy slot components
      lines.push(
        '// Import admin modules (AgentAdminExport contract + legacy slot registration)',
      );

      const adminImports: Array<{ agentClass: string; adminVar: string }> = [];

      for (const { packageName, agentClass, hasAdmin } of registrations) {
        if (hasAdmin) {
          const adminVar = `admin_${toSafeIdentifier(packageName)}`;
          // Use namespace import so missing optional exports (navItems) resolve
          // as undefined instead of throwing a hard ESM error.
          lines.push(`import * as ${adminVar} from '${packageName}/admin';`);
          adminImports.push({ agentClass: agentClass!, adminVar });
        } else {
          lines.push(
            `// ${packageName}/admin — not available (no ./admin export)`,
          );
        }
      }

      lines.push('');

      // Build the agentAdminRegistry Map
      lines.push('// Agent admin registry: agentClass -> AgentAdminExport');
      lines.push('export const agentAdminRegistry = new Map();');
      for (const { agentClass, adminVar } of adminImports) {
        const key = JSON.stringify(agentClass);
        lines.push(
          `if (${adminVar}.default) {`,
          `  agentAdminRegistry.set(${key}, {`,
          `    default: ${adminVar}.default,`,
          `    createAPIClient: ${adminVar}.createAPIClient,`,
          `    navItems: ${adminVar}.navItems,`,
          `  });`,
          `}`,
        );
      }

      lines.push('');
      lines.push('// Export for explicit initialization if needed');
      lines.push('export function initializeAgentRegistrations() {}');
      lines.push('');

      return lines.join('\n');
    },
  };
}
